import { db } from "../firebase-config"; // Adjust path as necessary
import { doc, getDoc, setDoc } from "firebase/firestore";
import * as Sentry from "@sentry/react"; // Assuming Sentry is configured globally or passed if needed

/**
 * Generates a short, unique code and maps it to a Firestore document reference.
 * Stores the mapping in a specific 'job/5tdpJAv6JNwmawkDKPSH' document.
 * @param {DocumentReference} docRef - The Firestore document reference to map the short code to.
 * @returns {Promise<string|null>} The generated short code or null if an error occurs.
 */
export const generateShortCode = async (docRef) => {
  try {
    // Consider making this path configurable or a constant
    const applicationUrlIdDocRef = doc(db, "job", "5tdpJAv6JNwmawkDKPSH");
    const applicationUrlIdDoc = await getDoc(applicationUrlIdDocRef);

    if (applicationUrlIdDoc.exists()) {
      const data = applicationUrlIdDoc.data();
      let attempts = 0;
      const maxAttempts = 10; // Prevent infinite loops

      while (attempts < maxAttempts) {
        // Generate a 6-character base-36 random string
        const applicationUrlId = Math.random().toString(36).substring(2, 8);

        // Check if the generated ID already exists in the mapping document
        if (!data || !data[applicationUrlId]) {
          // If it doesn't exist, store the mapping (docRef path or ID)
          await setDoc(
            applicationUrlIdDocRef,
            // Store the path or ID for easier lookup if needed, adjust as per your needs
            { [applicationUrlId]: docRef.path },
            { merge: true }
          );
          return applicationUrlId; // Return the unique code
        }
        attempts++;
      }
      // If max attempts reached without finding a unique code
      const error = new Error("Failed to generate a unique short code after multiple attempts.");
      console.error(error.message);
      if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
        Sentry.captureException(error);
      }
      return null; // Indicate failure

    } else {
      // The mapping document itself doesn't exist - this is a critical setup issue.
      const error = new Error("Critical Error: Short code mapping document 'job/5tdpJAv6JNwmawkDKPSH' not found!");
      console.error(error.message);
       if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
         Sentry.captureException(error);
       }
      // Depending on requirements, you might want to throw or attempt to create the document here.
      return null; // Indicate failure
    }
  } catch (error) {
    console.error("Error generating short code:", error);
    if (process.env.REACT_APP_ENVIRONEMENT === "prod") {
      Sentry.captureException(error);
    }
    return null; // Indicate failure
  }
};